import {
    ArrowsLeftRight,
    Check,
    Eye,
    Lightning,
    PencilSimple,
    Plus,
    Trash,
    TrendUp,
    Warning,
    X
} from "@phosphor-icons/react";
import dayjs from "dayjs";
import { Badge, Spinner, Tooltip } from "flowbite-react";
import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { httpService } from "../../../../services/httpService";
import AnimatedModal from "../../../../components/AnimatedModal";
import { useDeleteIntegration } from "../../../../hooks/delete/useDeleteIntegration";
import { clearIncompleteIntegration } from "../../../../hooks";
import { getTimezoneOffsetString } from "../../../../lib/utils";
import { API_CONFIG, LOCAL_STORAGE_KEYS, PLATFORM_ICON_MAP, PLATFORM_LABEL_MAPPER } from "../../../../shared/constants";
import { IIntegrations, IIntegrationsSet } from "../interface";
import { getStatusStyles, IntegrationStatusBadge } from "./SourceConfigurationUtils";
import { useNavigate } from "react-router-dom";

interface IntegrationCardProps {
    integrationSet: IIntegrations;
    onEdit: () => void;
    onDeleteAccount: (accountId: number | string, direction: "inward" | "outward", trackingId: string) => void;
    onUpdateName: (newName: string) => Promise<void>;
    hasDeletePermission: boolean;
    trackingId: string;
}

const IntegrationCard: React.FC<IntegrationCardProps> = ({
    integrationSet,
    onEdit,
    onDeleteAccount,
    onUpdateName,
    hasDeletePermission,
    trackingId,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [filterType, setFilterType] = useState<string | null>(null);
    const [filterDirection, setFilterDirection] = useState<"inward" | "outward" | null>(null);
    const { name, inwards, outwards } = integrationSet;
    const [isEditingName, setIsEditingName] = useState(false);
    const [editedName, setEditedName] = useState(name || "");
    const [isSavingName, setIsSavingName] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const queryClient = useQueryClient();

    React.useEffect(() => {
        if (!isDetailModalOpen) return;

        const currentInwards = inwards || [];
        const currentOutwards = outwards || [];

        if (filterType && filterDirection) {
            const sources = filterDirection === "inward" ? currentInwards : currentOutwards;
            const remainingOfType = sources.filter(s => s.type === filterType);
            if (remainingOfType.length === 0) {
                handleCloseModal();
            }
        } else {
            // Summary mode - close if both are empty
            if (currentInwards.length === 0 && currentOutwards.length === 0) {
                handleCloseModal();
            }
        }
    }, [inwards, outwards, filterType, filterDirection, isDetailModalOpen]);

    const deleteIntegrationMutation = useDeleteIntegration();

    const handleConfirmDelete = async () => {
        try {
            if (integrationSet.is_ongoing) {
                await httpService.delete(`${API_CONFIG.path.trackingIntegration}/${trackingId}/delete-all`);
                toast.success(t("configurations.integrations.ongoingIntegrationDeleted", "Ongoing integration deleted successfully"));
                queryClient.invalidateQueries({ queryKey: ["integrations"] });

                // Clear incomplete integration tracking when deleting
                clearIncompleteIntegration(trackingId);
            } else {
                await deleteIntegrationMutation.mutateAsync(trackingId);
                toast.success(t("configurations.integrations.integrationDeleted", "Integration deleted successfully"));
            }
            setIsDeleteModalOpen(false);
        } catch (error) {
            console.error("Failed to delete integration", error);
            toast.error(t("configurations.integrations.deleteIntegrationError", "Failed to delete integration"));
        }
    };

    const integrationDate = inwards[0]?.created_at || outwards[0]?.created_at;

    const handleSaveName = async () => {
        if (editedName.trim() === name) {
            setIsEditingName(false);
            return;
        }
        setIsSavingName(true);
        try {
            await onUpdateName(editedName.trim());
            setIsEditingName(false);
        } catch (error) {
            console.error("Failed to update name", error);
        } finally {
            setIsSavingName(false);
        }
    };

    const getSourceIcons = (sources: IIntegrationsSet[], direction: "inward" | "outward") => {
        // Group by type and count
        const grouped = sources.reduce((acc, source) => {
            if (!acc[source.type]) {
                acc[source.type] = {
                    source,
                    count: 0,
                    statusOrder: ["failed", "in_progress", "pending", "finished"]
                };
            }
            acc[source.type].count += 1;
            // The worst status wins for the group's border style
            const currentStatus = source.status || "finished";
            const existingStatus = acc[source.type].source.status || "finished";
            if (acc[source.type].statusOrder.indexOf(currentStatus) < acc[source.type].statusOrder.indexOf(existingStatus)) {
                acc[source.type].source.status = currentStatus;
            }
            return acc;
        }, {} as Record<string, { source: IIntegrationsSet; count: number; statusOrder: string[] }>);

        return (
            <div className="flex gap-2">
                {Object.values(grouped).map(({ source, count }, idx) => {
                    const statusStyle = getStatusStyles(source.status, t);
                    // const statusStyle = getStatusStyles("in_progress");
                    return (
                        <Tooltip
                            key={`${source.type}-${idx}`}
                            content={<div>
                                {statusStyle.loading ? <p>{t('configurations.integrations.syncing')}</p> : <p>{PLATFORM_LABEL_MAPPER[source.type]} ({t('configurations.integrations.accountsCount', { count })})</p>}
                            </div>}
                            placement="auto"
                            arrow={true}
                        >
                            <div
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setFilterType(source.type);
                                    setFilterDirection(direction);
                                    setIsDetailModalOpen(true);
                                }}
                                className={`relative inline-block h-11 w-11 rounded-xl bg-white p-1 shadow-sm border ${statusStyle.border} cursor-pointer transition-all duration-300 hover:scale-102 hover:shadow-md active:scale-95 flex items-center justify-center`}
                            >
                                <img
                                    src={PLATFORM_ICON_MAP[source.type] || ""}
                                    alt={source.type}
                                    className={`h-full w-full object-contain transition-all duration-300 ${statusStyle.loading ? 'scale-[0.35] opacity-80' : 'scale-100 opacity-100'}`}
                                />

                                {count > 1 && source.status !== "in_progress" && (
                                    <div className="absolute -top-1.5 -left-1.5 h-4 w-4 rounded-full bg-indigo-600 text-[9px] font-bold text-white flex items-center justify-center ring-2 ring-white">
                                        {count}
                                    </div>
                                )}

                                {statusStyle.loading && (
                                    <div className="absolute inset-0 flex items-center justify-center animate-pulse">
                                        <Spinner size="lg" className="opacity-40" />
                                    </div>
                                )}

                                {statusStyle.dot && !statusStyle.loading && (
                                    <div className={`absolute -top-1 -right-1 h-3 w-3 rounded-full border-2 border-white ${statusStyle.bg} ${statusStyle.animation || ''}`} />
                                )}
                            </div>
                        </Tooltip>
                    );
                })}
                {sources.length < 3 && (
                    <Tooltip
                        content={<div>
                            <p className="font-bold">{t('configurations.integrations.addAccount')}</p>
                        </div>}
                        placement="top"
                    >
                        <div
                            onClick={(e) => {
                                e.stopPropagation();
                                localStorage.setItem(LOCAL_STORAGE_KEYS.INTEGRATION_MODE, "edit");
                                setTimeout(() => {
                                    navigate(`/integrations?trackingId=${trackingId}`);
                                }, 100);
                            }}
                            className={`relative h-11 w-11 rounded-xl bg-white p-1 shadow-sm border border-dashed border-gray-200 cursor-pointer transition-all duration-300 hover:scale-102 hover:shadow-md active:scale-95 flex items-center justify-center`}
                        >
                            <Plus size={25} className="transition-all duration-300 text-gray-300" />
                        </div>
                    </Tooltip>
                )}
            </div>
        );
    };

    const SourceDetailCard: React.FC<{ source: IIntegrationsSet; direction: "inward" | "outward" }> = ({ source, direction }) => {
        const sourceName = source.ads_parent_account_name || source.ads_sub_account_name || source.user_name || t('common.na');
        const statusStyle = getStatusStyles(source.status, t);
        const timezone = source.timezone_str
            ? `${source.timezone_str} (UTC${getTimezoneOffsetString(source.timezone_str)})`
            : t('common.na');
        const email = source.email || t('common.na');
        const accountId = source.ads_parent_id || source.admob_publisher_id || source.apple_iap_vendor_number || source.apple_ads_client_id || source.ads_sub_account_id || t('common.na');

        return (
            <div
                className={`group relative rounded-xl border border-gray-100 p-2 transition-all bg-white shadow-md  min-h-[125px] ${statusStyle.loading ? 'animate-pulse' : ''}`}
            >
                {/* Advanced Status Background Glow */}
                {source.status === "failed" && <div className="absolute inset-0 bg-red-500/5 animate-pulse pointer-events-none" />}
                {source.status === "pending" && <div className="absolute inset-0 bg-amber-500/5 animate-pulse pointer-events-none [animation-duration:3000ms]" />}

                <div className="flex items-start gap-3 relative z-10">
                    {/* Platform Icon with Status Ring */}
                    <div className={`relative shrink-0 h-10 w-10 rounded-lg bg-white p-1.5 shadow-sm border ${statusStyle.border} flex items-center justify-center transition-all duration-500 group-hover:scale-102`}>
                        <img
                            src={PLATFORM_ICON_MAP[source.type] || ""}
                            alt={source.type}
                            className={`h-full w-full object-contain transition-all duration-300 ${statusStyle.loading ? 'scale-50 opacity-70' : ''}`}
                        />
                        {statusStyle.loading && (
                            <div className="absolute inset-0 flex items-center justify-center -top-[1px]">
                                <Spinner size="md" className="opacity-40 text-blue-500" />
                            </div>
                        )}
                        {statusStyle.dot && !statusStyle.loading && (
                            <div className={`absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full border-2 border-white ${statusStyle.bg} shadow-sm ${statusStyle.animation || ''}`} />
                        )}
                    </div>

                    <div className="flex-1 min-w-0 space-y-1">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <span className="text-[9px] font-bold text-gray-500 uppercase tracking-widest">
                                    {source.type.replace("_", " ")}
                                </span>
                                <IntegrationStatusBadge status={source.status} />
                            </div>
                            {hasDeletePermission && (
                                <button
                                    onClick={() => onDeleteAccount(source.id || "", direction, trackingId)}
                                    className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-all active:scale-90"
                                    title={t("configurations.integrations.deleteAccount")}
                                >
                                    <Trash size={14} weight="bold" />
                                </button>
                            )}
                        </div>
                        <p className="text-sm font-bold text-gray-900 truncate flex items-center gap-1.5">
                            {sourceName}
                            <span className="text-[10px] text-gray-500 font-medium">{timezone}</span>
                        </p>
                        <div className="text-[11px] space-y-0.5">
                            {email !== t('common.na') && (
                                <p className="text-gray-900 truncate flex items-center gap-1">
                                    <span className="text-gray-500 font-bold">{t('configurations.integrations.email')}:</span>
                                    <span className="font-medium text-gray-700">{email}</span>
                                </p>
                            )}
                            {accountId !== t('common.na') && (
                                <p className="text-gray-900 truncate flex items-center gap-1">
                                    <span className="text-gray-500 font-bold">{t('configurations.integrations.id')}</span>
                                    <span className="font-medium text-gray-700">{accountId}</span>
                                </p>
                            )}
                            {source.created_at && (
                                <p className="text-gray-900 truncate flex items-center gap-1">
                                    <span className="text-gray-500 font-bold">{t('configurations.integrations.date')}</span>
                                    <span className="font-medium text-gray-700">{dayjs(source.created_at).format("MMM D, YYYY")}</span>
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };


    const renderAccountDetails = (source: IIntegrationsSet, direction: "inward" | "outward") => {
        return <SourceDetailCard key={source.social_account_id || source.id} source={source} direction={direction} />;
    };

    const handleCloseModal = () => {
        setIsDetailModalOpen(false);
        // Delay resetting selection to allow for smooth exit animation
        setTimeout(() => {
            setFilterType(null);
            setFilterDirection(null);
        }, 300);
    };

    return (
        <>
            <div
                className={`flex flex-col border rounded-2xl shadow-md transition-all duration-300  group/card relative ${integrationSet.is_ongoing ? 'bg-amber-50 border-amber-200 cursor-pointer hover:shadow-lg hover:border-amber-300' : 'bg-white border-gray-200'}`}
            >
                {integrationSet.is_ongoing && (
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 text-xs text-amber-600 bg-amber-100/50 rounded-full px-2 py-0.5">
                        {t("configurations.integrations.actionRequired")}
                    </div>
                )}
                {integrationSet.is_ongoing && <div className="absolute top-0 left-0 w-full h-full bg-transparent z-10" onClick={() => {
                    if (integrationSet.is_ongoing) {
                        localStorage.setItem(LOCAL_STORAGE_KEYS.INTEGRATION_MODE, 'create');
                        navigate(`/integrations?trackingId=${trackingId}`);
                    }
                }}></div>}
                {/* Compact Header */}
                <div className={`px-4 py-2 border-b ${integrationSet.is_ongoing ? 'border-amber-100 bg-amber-100/50' : 'border-gray-50 bg-gray-100'}`}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="flex flex-col min-w-0 ">
                                {isEditingName ? (
                                    <div className="relative flex-1 max-w-full inline-grid items-center">
                                        {/* Invisible ghost text for auto-sizing */}
                                        <span className="invisible whitespace-pre text-sm font-bold px-1 col-start-1 row-start-1 w-fit">
                                            {editedName || " "}
                                        </span>
                                        <input
                                            type="text"
                                            value={editedName}
                                            onChange={(e) => setEditedName(e.target.value)}
                                            className="col-start-1 row-start-1 w-full bg-transparent border-none p-0 text-sm font-bold text-gray-900 focus:ring-0 focus:outline-none"
                                            autoFocus
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') handleSaveName();
                                                if (e.key === 'Escape') {
                                                    setIsEditingName(false);
                                                    setEditedName(name || "");
                                                }
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </div>
                                ) : (
                                    <div
                                        className="flex items-center gap-2 group/name cursor-pointer"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setIsEditingName(true);
                                        }}
                                    >
                                        <h3 className="text-sm font-bold text-gray-600 truncate">
                                            {name}
                                        </h3>
                                        <PencilSimple
                                            size={14}
                                            className="text-gray-400"
                                        />
                                    </div>
                                )}
                                <p className="text-[10px] text-gray-500">
                                    {integrationDate ? t("configurations.integrations.integrated_on", { date: dayjs(integrationDate).format("MMM D, YYYY") }) : t('common.na')}
                                </p>
                            </div>
                        </div>

                        {/* Header Actions */}
                        <div className="flex items-center gap-1 ml-2">
                            {isEditingName ? (
                                <>
                                    <Tooltip content={t("configurations.integrations.saveName")}>
                                        <button
                                            onClick={handleSaveName}
                                            disabled={isSavingName}
                                            className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                        >
                                            <Check size={18} weight="bold" />
                                        </button>
                                    </Tooltip>
                                    <Tooltip content={t("configurations.integrations.cancelEditing")}>
                                        <button
                                            onClick={() => {
                                                setIsEditingName(false);
                                                setEditedName(name || "");
                                            }}
                                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                        >
                                            <X size={18} weight="bold" />
                                        </button>
                                    </Tooltip>
                                </>
                            ) : (
                                <>
                                    <Tooltip content={t("configurations.integrations.addAccountTooltip")}>
                                        <button
                                            onClick={() => {
                                                onEdit();
                                            }}
                                            className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                        >
                                            <Plus size={18} />
                                        </button>
                                    </Tooltip>
                                    <Tooltip content={t("configurations.integrations.viewPairSummary")}>
                                        <button
                                            onClick={() => {
                                                setFilterType(null);
                                                setFilterDirection(null);
                                                setIsDetailModalOpen(true);
                                            }}
                                            className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                        >
                                            <Eye size={18} />
                                        </button>
                                    </Tooltip>
                                    <Tooltip content={t("configurations.integrations.deletePair")}>
                                        <button
                                            onClick={() => {
                                                setIsDeleteModalOpen(true);
                                            }}
                                            className={`p-1.5 rounded-lg transition-colors ${integrationSet.is_ongoing ? 'text-amber-600 hover:bg-amber-100' : 'text-gray-400 hover:text-red-600 hover:bg-red-50'}`}
                                        >
                                            <Trash size={18} className={integrationSet.is_ongoing ? '' : 'text-red-400'} />
                                        </button>
                                    </Tooltip>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Integration Icons Row */}
                <div className="p-4 bg-gray-50/30 relative">

                    <div className="flex items-center justify-center gap-4">
                        <div className="border border-gray-200 p-3 rounded-lg flex items-center  gap-4 w-fit relative">
                            {getSourceIcons(inwards as IIntegrationsSet[], "inward")}
                            <div className="absolute left-2 -top-[8px] text-[10px] text-indigo-600 bg-white px-[2px] py-0 tracking-widest font-bold" > {t("configurations.integrations.spend")}</div>
                        </div>
                        <div className="flex items-center justify-center p-1.5 rounded-full bg-white shadow-sm border border-gray-100">
                            <ArrowsLeftRight size={14} className="text-indigo-400" weight="bold" />
                        </div>
                        <div className="border border-gray-200 p-3 rounded-lg flex items-center  gap-4 w-fit relative">
                            {getSourceIcons(outwards, "outward")}
                            <div className="absolute left-2 -top-[8px] text-[10px] text-green-600 bg-white px-[2px] py-0 tracking-widest font-bold" > {t("configurations.integrations.revenue")}</div>
                        </div>
                    </div>
                </div>
                {integrationSet.is_ongoing && (
                    <div className="px-2 mb-2 bg-transparent flex items-center gap-2">
                        <Warning size={16} className="text-amber-600 shrink-0" weight="fill" />
                        <p className="text-[11px] font-medium text-amber-800">
                            {t("configurations.integrations.almostThereActionNote")}
                        </p>
                    </div>
                )}
            </div>

            {/* Detailed View Modal */}
            <AnimatedModal
                show={isDetailModalOpen}
                onClose={handleCloseModal}
                title={filterType ? t("configurations.integrations.sourceDetails", { platform: PLATFORM_LABEL_MAPPER[filterType] }) : t("configurations.integrations.pairSummary", { name })}
                size={filterType ? "md" : "xl"}
                bodyClassName="p-2"
            >
                {filterType && filterDirection ? (
                    <div className="space-y-4 p-2">
                        <div className="space-y-3 max-h-[600px] custom-scrollbar pr-1 pb-[5px] overflow-y-auto">
                            {(filterDirection === "inward" ? inwards : outwards)
                                .filter(s => s.type === filterType)
                                .map((s) => renderAccountDetails(s, filterDirection))}
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 p-2 ">
                            {/* Spending Sources */}
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <h4 className="flex items-center gap-2 text-xs font-bold text-indigo-600 uppercase tracking-widest">
                                        <Lightning weight="duotone" size={16} />
                                        {t("configurations.integrations.spendingChannels")}
                                    </h4>
                                    <Badge color="indigo" size="xs" className="rounded-full">
                                        {inwards.length}
                                    </Badge>
                                </div>
                                <div className="space-y-3 max-h-[400px] overflow-y-auto custom-scrollbar pr-1 pb-[5px]">
                                    {inwards.length > 0 ? (
                                        inwards.map((s) => renderAccountDetails(s, "inward"))
                                    ) : (
                                        <p className="text-sm text-gray-400 italic py-8 text-center bg-gray-50 rounded-lg">{t("configurations.integrations.noAccounts")}</p>
                                    )}
                                </div>
                            </div>

                            {/* Revenue Sources */}
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <h4 className="flex items-center gap-2 text-xs font-bold text-emerald-600 uppercase tracking-widest">
                                        <TrendUp weight="duotone" size={16} />
                                        {t("configurations.integrations.revenueSources")}
                                    </h4>
                                    <Badge color="green" size="xs" className="rounded-full">
                                        {outwards.length}
                                    </Badge>
                                </div>
                                <div className="space-y-3 max-h-[400px] overflow-y-auto custom-scrollbar pr-1 pb-[5px]">
                                    {outwards.length > 0 ? (
                                        outwards.map((o) => renderAccountDetails(o, "outward"))
                                    ) : (
                                        <p className="text-sm text-gray-400 italic py-8 text-center bg-gray-50 rounded-lg">{t("configurations.integrations.noAccounts")}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </AnimatedModal>

            {/* Delete Confirmation Modal */}
            <AnimatedModal
                show={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                title={t("configurations.integrations.deleteIntegrationTitle", "Delete Integration")}
                isConfirmation={true}
                onConfirm={handleConfirmDelete}
                confirmText={t("common.delete", "Delete")}
                isConfirming={deleteIntegrationMutation.isPending}
                confirmButtonColor="red"
            >
                <div className="text-center p-4">
                    <p className="text-sm text-gray-600">
                        {t("configurations.integrations.deleteIntegrationConfirmation", "Are you sure you want to delete this integration pair? This action cannot be undone.")}
                    </p>
                    <p className="mt-2 font-bold text-gray-900">{name}</p>
                </div>
            </AnimatedModal>
        </>
    );
};

export default IntegrationCard;
